import React, { useCallback } from 'react';
import { Box, Button, Typography } from '@mui/material';
import { AutoAwesome as CustomTemplateIcon, CloudUpload as UploadIcon } from '@mui/icons-material';

interface CsvDropZoneProps {
  onFileSelected: (file: File) => void;
  disabled?: boolean;
  onError?: (message: string) => void;
  onCreateCustomTemplate: () => void;
}

const ACCEPTED_EXTENSIONS = ['.csv', '.tsv'];

const isAcceptedFile = (file: File): boolean =>
  ACCEPTED_EXTENSIONS.some(ext => file.name.toLowerCase().endsWith(ext));

const CsvDropZone: React.FC<CsvDropZoneProps> = ({
  onFileSelected,
  disabled = false,
  onError,
  onCreateCustomTemplate,
}) => {
  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      if (isAcceptedFile(file)) {
        onFileSelected(file);
      } else {
        onError?.('Invalid file type. Please upload a CSV or TSV file.');
      }
    }
    // Reset input so re-selecting the same file triggers onChange
    event.target.value = '';
  };

  const handleDrop = useCallback(
    (event: React.DragEvent<HTMLDivElement>) => {
      event.preventDefault();
      event.stopPropagation();
      if (disabled) return;

      const file = event.dataTransfer.files?.[0];
      if (file) {
        if (isAcceptedFile(file)) {
          onFileSelected(file);
        } else {
          onError?.('Invalid file type. Please upload a CSV or TSV file.');
        }
      }
    },
    [disabled, onFileSelected, onError],
  );

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
  };

  return (
    <Box>
      <Box
        component="label"
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          border: '2px dashed',
          borderColor: 'divider',
          borderRadius: 1,
          p: 6,
          textAlign: 'center',
          cursor: disabled ? 'default' : 'pointer',
          opacity: disabled ? 0.5 : 1,
          '&:hover': disabled ? {} : { borderColor: 'primary.main', bgcolor: 'action.hover' },
        }}
      >
        <UploadIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
        <Typography variant="h6" gutterBottom>
          Upload CSV or TSV File
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Drag &amp; drop or click to select
        </Typography>
        <input
          type="file"
          hidden
          accept=".csv,.tsv"
          onChange={handleFileSelect}
          disabled={disabled}
          aria-label="Select CSV or TSV file for upload"
        />
      </Box>

      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
        <Button
          variant="outlined"
          startIcon={<CustomTemplateIcon />}
          onClick={onCreateCustomTemplate}
          disabled={disabled}
        >
          Create Custom Template
        </Button>
      </Box>
    </Box>
  );
};

export default CsvDropZone;
